import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input.tsx'
import { Label } from '@/components/ui/label.tsx'
import { useToast } from '@/hooks/use-toast'
import { logger } from '@/lib/logger'
import { useAuth } from '@/providers/auth-provider'
import { useAuthStore } from '@/stores/auth-store'
import { AtSign, Eye, EyeOff, Loader2 } from 'lucide-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

// Simple validation replacement for now
const validateLogin = (email: string, password: string, t: (key: string) => string) => {
  const errors: { email?: string; password?: string } = {}
  if (!email || !email.includes('@')) errors.email = t('loginPage.invalidEmail')
  if (!password || password.length < 6) errors.password = t('loginPage.passwordTooShort')
  return { isValid: Object.keys(errors).length === 0, errors }
}

interface Particle {
  id: number
  x: number
  y: number
  size: number
  color: string
  opacity: number
  xOffset: number
  yOffset: number
  transitionSpeed: number
  floatDuration: number
  floatDelay: number
}

export default function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { autoLogin } = useAuth()
  const userLogin = useAuthStore((state) => state.userLogin)

  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [particles, setParticles] = useState<Particle[]>([])
  const particleContainer = useRef<HTMLDivElement>(null)

  // Form state
  const [formData, setFormData] = useState({ email: '', password: '' })
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({})

  // Create interactive background particles
  const createParticles = useCallback(() => {
    const primaryColor = '#6F59FD'
    const secondaryColor = '#ff6f60'
    const particleCount = 30

    const newParticles: Particle[] = []

    for (let i = 0; i < particleCount; i++) {
      newParticles.push({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 220 + 80,
        color: Math.random() > 0.5 ? primaryColor + '35' : secondaryColor + '28',
        opacity: Math.random() * 0.55 + 0.15,
        xOffset: 0,
        yOffset: 0,
        transitionSpeed: Math.random() * 150 + 80,
        floatDuration: Math.random() * 6 + 6,
        floatDelay: Math.random() * -8
      })
    }

    setParticles(newParticles)
  }, [])

  // Handle mouse movement for interactive effect
  const handleMouseMove = useCallback((event: React.MouseEvent) => {
    if (!particleContainer.current) return

    const rect = particleContainer.current.getBoundingClientRect()
    const mouseX = event.clientX - rect.left
    const mouseY = event.clientY - rect.top

    const centerX = rect.width / 2
    const centerY = rect.height / 2

    // Calculate offset from center (normalized)
    const offsetX = (mouseX - centerX) / centerX
    const offsetY = (mouseY - centerY) / centerY

    // Update particles position based on mouse movement
    setParticles(prev => prev.map(particle => {
      // Calculate movement factor based on position (3D-like effect)
      const moveFactor = 40 * (particle.size / 150)

      return {
        ...particle,
        xOffset: -offsetX * moveFactor,
        yOffset: -offsetY * moveFactor
      }
    }))
  }, [])

  // Regular login function
  const onSubmit = useCallback(async (data: { email: string; password: string }) => {
    setIsLoading(true)

    // Save email to localStorage for next time
    localStorage.setItem('user_email', data.email)

    try {
      await userLogin({
        email: data.email,
        password: data.password
      })

      // Show success notification
      toast({
        title: t('success.title'),
        description: t('loginPage.loginSuccess')
      })

      // Trigger AuthProvider to load user from token
      logger.debug('LOGIN: Triggering AuthProvider autoLogin')
      await autoLogin()
      logger.debug('LOGIN: AuthProvider autoLogin completed')

      // Initialize app stores BEFORE navigation
      try {
        const { useConfigurationStore } = await import('@/stores/configuration-store')
        const { useCustomerStore } = await import('@/stores/customer-store')
        const { useDevicesStore } = await import('@/stores/devices-store')
        const { useAuthStore } = await import('@/stores/auth-store')

        const configStore = useConfigurationStore.getState()
        const customerStore = useCustomerStore.getState()
        const devicesStore = useDevicesStore.getState()
        const authStore = useAuthStore.getState()

        // Get auth headers for configuration
        const headers = authStore.getApiHeaders()

        logger.debug('LOGIN: Initializing stores after login')

        // Initialize configuration (languages, voices)
        await configStore.initBaseConfiguration(headers)
        logger.debug('LOGIN: Configuration initialized')

        // Initialize devices (critical for data loading!)
        await devicesStore.initializeDevices()
        logger.debug('LOGIN: Devices initialized')

        // Reset customer store
        await customerStore.reset()
        logger.debug('LOGIN: Customer store reset')

      } catch (error) {
        logger.error('LOGIN: Error initializing app stores', error as Error)
        toast({
          title: t('error.title'),
          description: t('loginPage.storeInitError'),
          variant: "destructive"
        })
      }

      // Small delay to ensure stores are ready
      await new Promise(resolve => setTimeout(resolve, 500))
      logger.debug('LOGIN: Navigating to activity page')

      // Navigate to main app after initialization
      navigate('/customer/activity', { replace: true })

    } catch (error) {
      // Clear password field on error for security
      setFormData(prev => ({ ...prev, password: '' }))

      // Detailed error handling based on different error types
      let errorMessage = t('loginPage.loginFailed')

      if (error instanceof Error) {
        if (error.message === 'UNKNOWN_ERROR') {
          errorMessage = t('loginPage.networkError')
        } else {
          errorMessage = error.message
        }
      }

      toast({
        title: t('loginPage.loginFailedTitle'),
        description: errorMessage,
        variant: "destructive"
      })
    } finally {
      setIsLoading(false)
    }
  }, [userLogin, navigate, toast])

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(prev => !prev)
  }, [])

  const handleFormSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate form
    const validation = validateLogin(formData.email, formData.password, t)
    setErrors(validation.errors)

    if (!validation.isValid) {
      return
    }

    await onSubmit(formData)
  }, [formData, onSubmit])

  useEffect(() => {
    // Load saved email from localStorage
    const savedEmail = localStorage.getItem('user_email')
    if (savedEmail) {
      setFormData({ email: savedEmail, password: '' })
    }

    // Auto-focus email field
    setTimeout(() => {
      const emailField = document.getElementById('email')
      if (emailField) {
        emailField.focus()
      }
    }, 100)

    // Initialize particles
    createParticles()

    // Keyboard event listeners
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && (e.target as HTMLElement).id === 'email') {
        setTimeout(() => {
          const passwordField = document.getElementById('password')
          if (passwordField) {
            passwordField.focus()
          }
        }, 100)
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [createParticles])

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-900 to-gray-900">
      <div className="min-h-screen flex flex-col items-center justify-center relative p-4 sm:p-6 lg:p-8 w-full">
        {/* Interactive background elements */}
        <div
          ref={particleContainer}
          className="fixed inset-0 overflow-hidden z-0"
          onMouseMove={handleMouseMove}
        >
          {/* Animated floating particles */}
          {particles.map((particle) => (
            <div
              key={particle.id}
              className="absolute rounded-full blur-2xl animate-float"
              style={{
                width: `${particle.size}px`,
                height: `${particle.size}px`,
                backgroundColor: particle.color,
                left: `${particle.x}%`,
                top: `${particle.y}%`,
                opacity: particle.opacity,
                '--float-duration': `${particle.floatDuration}s`,
                '--float-delay': `${particle.floatDelay}s`,
                transform: `translateX(${particle.xOffset}px) translateY(${particle.yOffset}px)`,
                transitionDuration: `${particle.transitionSpeed}ms`,
                transitionProperty: 'transform',
                transitionTimingFunction: 'ease-out',
              } as React.CSSProperties}
            />
          ))}
        </div>

        {/* Content container */}
        <div className="z-10 w-full max-w-sm sm:max-w-md lg:max-w-lg flex flex-col items-center relative">
          {/* Logo + Brand Name */}
          <div className="mb-8 sm:mb-10 flex flex-col items-center animate-fade-in-up">
            <a href="/" className="relative group mb-4">
              <div className="absolute -inset-3 bg-gradient-to-r from-brand-primary to-brand-secondary rounded-full blur-lg opacity-60 group-hover:opacity-100 transition duration-1000 animate-pulse-glow"></div>
              <div className="relative">
                <img
                  className="w-14 h-14 sm:w-18 sm:h-18 lg:w-22 lg:h-22 z-10 drop-shadow-2xl"
                  src="/src/assets/assistentAiSignet.svg"
                  alt="Assistent.ai Logo"
                />
              </div>
            </a>
            <span className="text-white/80 text-sm sm:text-base font-light tracking-widest uppercase">Assistent.ai</span>
          </div>

          {/* Auth Card */}
          <div className="animate-fade-in-up-delay bg-white/[0.07] backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-[0_8px_60px_-12px_rgba(111,89,253,0.25)] px-6 sm:px-8 lg:px-10 py-8 sm:py-10 lg:py-12 w-full border border-white/[0.12] ring-1 ring-white/[0.05]">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-center mb-6 sm:mb-8 lg:mb-10 text-white tracking-tight">
              {t('login.loginNow')}
            </h1>

            <form onSubmit={handleFormSubmit} className="space-y-6">
              {/* Email Field */}
              <div className="space-y-1">
                <div className="relative group">
                  <Label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                    {t('global.fieldnames.email')}
                  </Label>
                  <div className="relative">
                    <Input
                      id="email"
                      type="email"
                      placeholder={t('global.fieldnames.email')}
                      autoComplete="email"
                      disabled={isLoading}
                      className={`
                        block w-full py-3.5 pl-4 pr-12 text-gray-100 bg-white/5 border border-gray-500/30 rounded-xl shadow-inner 
                        focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-colors 
                        focus-visible:ring-2 focus-visible:ring-brand-primary
                        ${errors.email ? 'border-red-500/50 focus:ring-red-500 focus:border-red-500' : ''}
                      `}
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <AtSign className={`h-5 w-5 ${errors.email ? 'text-red-400' : 'text-gray-400'}`} />
                    </div>

                    {/* Visual highlight effect on focus */}
                    <div className="absolute inset-0 rounded-xl pointer-events-none opacity-0 group-focus-within:opacity-20 bg-gradient-to-r from-brand-primary to-brand-secondary transition-opacity"></div>
                  </div>
                  {errors.email && (
                    <p className="text-sm text-red-400 mt-1">{errors.email}</p>
                  )}
                </div>
              </div>

              {/* Password Field */}
              <div className="space-y-1">
                <div className="relative group">
                  <Label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
                    {t('global.fieldnames.password')}
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder={t('global.fieldnames.password')}
                      autoComplete="current-password"
                      disabled={isLoading}
                      className={`
                        block w-full py-3.5 pl-4 pr-12 text-gray-100 bg-white/5 border border-gray-500/30 rounded-xl shadow-inner 
                        focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-colors 
                        focus-visible:ring-2 focus-visible:ring-brand-primary
                        ${errors.password ? 'border-red-500/50 focus:ring-red-500 focus:border-red-500' : ''}
                      `}
                      value={formData.password}
                      onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    />
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-gray-400 hover:text-white transition-colors focus:outline-none"
                      aria-label="Toggle password visibility"
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <Eye className="h-5 w-5" />
                      ) : (
                        <EyeOff className="h-5 w-5" />
                      )}
                    </button>

                    {/* Visual highlight effect on focus */}
                    <div className="absolute inset-0 rounded-xl pointer-events-none opacity-0 group-focus-within:opacity-20 bg-gradient-to-r from-brand-primary to-brand-secondary transition-opacity"></div>
                  </div>
                  {errors.password && (
                    <p className="text-sm text-red-400 mt-1">{errors.password}</p>
                  )}
                </div>
              </div>

              {/* Forgot Password Link */}
              <div className="text-right">
                <a href="#" className="text-sm text-gray-300 hover:text-brand-primary transition-colors">
                  {t('loginPage.forgotPassword')}
                </a>
              </div>

              {/* Login Button */}
              <div className="pt-3">
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="relative w-full flex justify-center py-4 px-4 border-0 rounded-xl text-white text-base font-semibold bg-gradient-to-r from-brand-primary to-brand-primary/80 hover:from-brand-primary hover:to-brand-secondary/70 shadow-[0_4px_24px_-4px_rgba(111,89,253,0.5)] hover:shadow-[0_4px_32px_-4px_rgba(111,89,253,0.7)] focus:outline-none focus:ring-2 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-primary transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <Loader2 className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" />
                      <span>{t('global.button.loading')}</span>
                    </div>
                  ) : (
                    <span>{t('loginPage.login')}</span>
                  )}
                </Button>
              </div>

            </form>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-xs text-gray-500 text-center z-10 relative animate-fade-in">
          <p>&copy; {new Date().getFullYear()} Assistent.ai</p>
        </div>
      </div>
    </div>
  )
}
